import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, forwardRef, inject, input, model, output } from '@angular/core';
import type { FormValueControl, ValidationError } from '@angular/forms/signals';
import { FALLBACK_WORK_WEEK, ScheduleExceptions, ScheduleKind, ScheduleWeekday } from '@jms/model/scheduling';
import { ScheduleCardComponent } from './card/schedule.card.component';
import { effectiveScheduleRule } from '@jms/model/scheduling';
import { SCHEDULE_TODAY, ScheduleCardContext } from './card/schedule.card.model';
import { SCHEDULE_EDITOR_CONTEXT, ScheduleEditorContext, ScheduleMode } from './editor/schedule.editor.context';
import { scheduleEditorLauncherProviders } from './editor/schedule.editor.launcher';
import { ScheduleEditorResult } from './editor/schedule.editor.launcher.contract';
import { ScheduleFieldContext, ScheduleValue } from './schedule.field.model';

/**
 * A schedule as one field: renders the card, opens its own pattern editor and exception popovers, and
 * hands back a single {@link ScheduleValue}. The host supplies what to evaluate against, never how.
 *
 * `mode: 'template'` is a statement about the host, not a preference: a reusable definition has no
 * calendar, so the card and editor anchor on today in UTC, the value never carries exceptions or
 * dates, and the editor removes the controls that would ask for them.
 */
@Component({
  selector: 'wb-schedule-field',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [ScheduleCardComponent],
  providers: [
    ...scheduleEditorLauncherProviders,
    {
      provide: SCHEDULE_EDITOR_CONTEXT,
      useFactory: (field: ScheduleFieldComponent): (() => ScheduleEditorContext) => () => field.editorContext(),
      deps: [forwardRef(() => ScheduleFieldComponent)],
    },
  ],
  template: '<wb-schedule-card [schedule]="$rule()" [exceptions]="$exceptions()" [context]="$cardContext()" [readonly]="$readonly()" (scheduleChange)="onScheduleChange($event)" (exceptionsChange)="onExceptionsChange($event)" />',
})
export class ScheduleFieldComponent implements FormValueControl<ScheduleValue | undefined> {

  private readonly todayProvider = inject(SCHEDULE_TODAY);

  public readonly value = model<ScheduleValue | undefined>(undefined);
  public readonly disabled = input(false);
  public readonly readonly = input(false);
  public readonly touch = output<void>();
  public readonly errors = input<readonly ValidationError.WithOptionalFieldTree[]>([]);

  public readonly $context = input<ScheduleFieldContext | undefined>(undefined, { alias: 'context' });

  public readonly $mode = input<ScheduleMode>('live', { alias: 'mode' });

  /** The kinds the host may store, or absent for all. Filtered out of the editor rather than disabled. */
  public readonly $allowedKinds = input<readonly ScheduleKind[] | undefined>(undefined, { alias: 'allowedKinds' });

  /** The calendar endings the host may store, or absent for all. */
  public readonly $allowedEndings = input<readonly string[] | undefined>(undefined, { alias: 'allowedEndings' });

  private readonly $isTemplate = computed(() => this.$mode() === 'template');

  protected readonly $readonly = computed(() => this.readonly() || this.disabled());

  protected readonly $rule = computed(() => effectiveScheduleRule(this.value()?.rule));

  private readonly $today = computed(() => this.$context()?.today ?? this.todayProvider());

  private readonly $workWeek = computed<ScheduleWeekday[]>(() => {

    const week = this.$context()?.workWeek;
    return [...(week?.length ? week : FALLBACK_WORK_WEEK)];
  });

  /**
   * Own exceptions with the inherited skips folded in, so the preview drops blacked-out dates and the
   * list shows them locked. Undefined on a template, which has no calendar to skip.
   */
  protected readonly $exceptions = computed<ScheduleExceptions | undefined>(() => {

    if (this.$isTemplate()) return undefined;

    const own = this.value()?.exceptions;
    const inherited = this.$context()?.inheritedSkips;

    return {
      ...(own?.skips ? { skips: own.skips } : {}),
      ...(own?.extras ? { extras: own.extras } : {}),
      ...(inherited?.line?.length ? { lineBlackouts: [...inherited.line] } : {}),
      ...(inherited?.plan?.length ? { planBlackouts: [...inherited.plan] } : {}),
    };
  });

  /**
   * The card previews from the LATER of the anchor and the value's own start, and is told `startsOn`
   * separately: a folded start cannot say whether an override exists, and the title needs exactly that.
   */
  protected readonly $cardContext = computed<ScheduleCardContext>(() => {

    const today = this.$today();
    if (this.$isTemplate()) return { effectiveFrom: today, timezone: 'UTC', today, workWeek: this.$workWeek() };

    const context = this.$context();
    const startsOn = this.value()?.startsOn;
    const anchor = context?.effectiveFrom;
    const effectiveFrom = startsOn && anchor && startsOn > anchor ? startsOn : anchor ?? startsOn ?? today;

    return {
      effectiveFrom,
      effectiveTo: context?.effectiveTo,
      timezone: context?.timezone ?? 'UTC',
      workWeek: this.$workWeek(),
      holidays: context?.holidays,
      today,
      startsOn,
      lastCompleted: this.value()?.lastCompleted,
    };
  });

  /**
   * What the pattern editor evaluates against. On a live host `effectiveFrom` is the unfolded anchor:
   * the editor offers the start override, so anchoring at a value the override already moved would
   * refuse every earlier date — including the anchor itself.
   */
  public editorContext(): ScheduleEditorContext {

    const context = this.$context();
    const today = this.$today();
    const shared = { taskName: context?.name ?? 'Task', today, workWeek: this.$workWeek(), allowedKinds: this.$allowedKinds(), allowedEndings: this.$allowedEndings() };

    if (this.$isTemplate()) return { ...shared, effectiveFrom: today, timezone: 'UTC', holidays: [], mode: 'template' };

    return { ...shared, effectiveFrom: context?.effectiveFrom ?? this.value()?.startsOn ?? today, effectiveTo: context?.effectiveTo, timezone: context?.timezone ?? 'UTC', holidays: [...(context?.holidays ?? [])] };
  }

  /** Every key of the editor's answer is authoritative: an absent date means "clear it". */
  protected onScheduleChange(result: ScheduleEditorResult): void {

    const current = this.value();
    const rule = effectiveScheduleRule(result.schedule);
    const next: ScheduleValue = this.$isTemplate() ? { ...current, rule } : { ...current, rule, startsOn: result.startsOn, lastCompleted: result.lastCompleted };

    this.value.set(next);
    this.touch.emit();
  }

  /** The card applies changes to the merged set; only the owned half belongs to the value. */
  protected onExceptionsChange(exceptions: ScheduleExceptions): void {

    if (this.$isTemplate()) return;

    this.value.set({ ...this.value(), exceptions: { skips: exceptions.skips, extras: exceptions.extras } });
    this.touch.emit();
  }
}
